import { Container, Rectangle } from "pixi.js"

// Registry + outside-world wiring for HELD speech bubbles -- the ones that grow a "Next" button and
// wait for the player instead of fading on a timer (see AvatarHud.spoke). The buttons themselves
// live inside each entity's HUD on the world-overlay surface, but two things about them can only be
// known from outside that HUD:
//
//   - THE PiP MUST NOT SHOW THE BUTTON. The follow-cam composites the WHOLE world-overlay layer
//     into its render texture, so a bubble's Next button would be drawn inside the PiP too -- where
//     it is a picture, not a control, and only covers the words. PixiJsClientRenderer hides every
//     live button for the duration of that render pass and shows them again straight after.
//
//   - PRESSING NEXT MUST NOT ALSO SWING. The click that fires a world attack arrives on a
//     document-level mousedown that cannot tell it landed on a pixi widget, so the press has to ask
//     the player controller to hold off, exactly as an interaction icon does (Player.suppressAttack).
//
// Statics rather than an injected collaborator because AvatarHud is built deep inside the sprite
// factories (LpcComposite/LpcLayered), which are handed neither the renderer nor the game logic --
// the same reason Mouse keeps its state static.
export class SpeechHoldButtons {
    // Only buttons that are currently ON SCREEN are in here: register() on show, unregister() on
    // dismiss and on HUD cleanup. So "hidden for capture" can flip them wholesale without having to
    // remember which ones were visible to begin with.
    private static buttons: Set<Container> = new Set()
    private static suppressAttack?: () => void

    // Called once by the renderer. Lazily reads the player controller: the controller does not exist
    // yet when the renderer is built.
    static bindSuppressAttack = (suppress: () => void) => {
        SpeechHoldButtons.suppressAttack = suppress
    }

    static register = (button: Container) => {
        SpeechHoldButtons.buttons.add(button)
    }

    static unregister = (button: Container) => {
        SpeechHoldButtons.buttons.delete(button)
    }

    // Hide/show every live Next button. Wrapped around the follow-cam's overlay render pass.
    static setHiddenForCapture = (hidden: boolean) => {
        SpeechHoldButtons.buttons.forEach(button => {
            button.visible = !hidden
        })
    }

    static notePressed = () => {
        SpeechHoldButtons.suppressAttack?.()
    }

    // Whether a held line is waiting on the player where they can read it: what the HUD steps back
    // for (gui/hud_presence.ts). A bubble the speaker took out of view with him (the Lord speaks at a
    // tree the player has not followed him to) is not one: dimming the HUD for it would leave the
    // player with less to look at and nothing to read. `view` is in global (stage) coordinates.
    static anyInView = (view: Rectangle) => {
        for (const button of SpeechHoldButtons.buttons) {
            if (!button.worldVisible) continue
            const b = button.getBounds()
            if (b.right > view.left && b.left < view.right && b.bottom > view.top && b.top < view.bottom) return true
        }
        return false
    }

    // Tests only.
    static liveCount = () => SpeechHoldButtons.buttons.size
}
